import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LtiService } from '../../lti/core/lti.service';
import { LtiMember } from '../../lti/shared/lti-member';
import { DialogService } from '../../rl-common/dialogs/core/dialog.service';
import { CustomButton } from '../../rl-common/dialogs/shared/custom-button';
import { CustomDialogWindow } from '../../rl-common/dialogs/shared/custom-dialog-window';
import { DateService } from '../../rl-common/shared/core/date.service';
import { UtilsService } from '../../rl-common/shared/core/utils.service';
import { Sort } from '../../rl-common/shared/shared/types/sort';
import { AssignmentService } from '../core/assignment.service';
import { Assignment } from '../shared/assignment';
import { StudentSubmission } from '../shared/student-submission';
import { Submission } from '../shared/submission';
import { SubmissionStatus } from '../shared/submission-status.enum';

@Component({
  selector: 'rl-assignment-statistics',
  templateUrl: './assignment-statistics.component.html',
  styleUrls: ['./assignment-statistics.component.scss', './assignment-statistics.component-rtl.css']
})
export class AssignmentStatisticsComponent implements OnInit {

  @Input('assignment') asgmt : Assignment;
  @Output() viewAssignment = new EventEmitter<void>();
  
  studentsList : {[userId : string] : StudentSubmission} = {};
  filteredStudentsList : StudentSubmission[] = [];
  studentsNames : string[] = [];
  sortBy : Sort = new Sort('user.family_name', 'asc');
  startGradingUrl : string;
  newGradeToUpdate : string;
  busy : boolean = true;
  blockView : boolean;
  recalcStats : boolean;
  postGradesConfirm : CustomDialogWindow = new CustomDialogWindow("postGrades.confirmDialog.title");
  postGradesTooltip : string;
  gradesToPost : number;

  constructor(private assignmentService : AssignmentService, private lti : LtiService, private utils : UtilsService, private dialogService: DialogService,
    private dateService : DateService, private translate : TranslateService) { }

  ngOnInit(): void {
    this.asgmt.statistics.total = 0;
    this.lti.getStudents().subscribe((students : LtiMember[]) => {
      students.forEach(s => {
        this.studentsList[s.user_id] = new StudentSubmission(s);
        this.studentsNames.push(s.name);
        this.asgmt.statistics.total++;
      });
      this.loadSubmissions();
    });

    this.initPostGradesPopup();

    this.postGradesTooltip = this.translate.instant('asgmt.view.postScores.tooltip').replace("#LMS_SYSTEM#", this.lti.getLMSSystemName());
  }

  loadSubmissions = () => this.assignmentService.getSubmissions(this.asgmt.id).subscribe(submissions => this.setSubmissions(submissions));

  setSubmissions(submissions : Submission[]){
    this.gradesToPost = 0;
    submissions.forEach(s => {
      let u = this.studentsList[s.lmsUserId];
      if(!u) return console.log("Student " + s.userId + " isn't found in students list");
      u.submission = s;
      if(u.status === SubmissionStatus.GRADED) this.gradesToPost++;
    });
    this.asgmt.statistics.didntSubmit = this.asgmt.statistics.total - (this.asgmt.statistics.graded + this.asgmt.statistics.needToGrade);
    this.filteredStudentsList = Object.values(this.studentsList);
    this.sort();
    if(submissions.some(s=> this.studentsList[s.lmsUserId])){
      this.startGradingUrl = this.addToUrl(document.location.href, "/submissions");
    }
    this.busy = false;
    if(this.recalcStats) this.recalcStatsNumbers();
  }

  filter(...status : string[]){
    this.filteredStudentsList = Object.values(this.studentsList);
    if(status.length){
      this.filteredStudentsList = this.filteredStudentsList.filter(s => status.indexOf(s.status) > -1);
    }
    this.sort();
  }

  search = (str : string) => {
    this.filteredStudentsList = Object.values(this.studentsList).filter(s => s.user.name.indexOf(str) > -1);
    this.sort();
  }

  sendToLms = () => {
    this.postGradesConfirm.message = this.translate.instant("postGrades.confirmDialog.msg").replace("#COUNT#", this.gradesToPost).replace("#LMS_SYSTEM#", this.lti.getLMSSystemName());
    this.dialogService.openDialog(this.postGradesConfirm);    
  }

  updateGrade(sub : StudentSubmission){
    let oldGrade = sub.submission.grade;
	if(!this.validateInput(this.newGradeToUpdate)){
		this.newGradeToUpdate =this.asgmt.lmsAssignmentMaxScore+'';
	}
    sub.submission.grade = this.newGradeToUpdate == '' ? undefined : <number><unknown>this.newGradeToUpdate;

    delete this.newGradeToUpdate;
    if(oldGrade === sub.submission.grade) return;

    this.updateGradesToPostCount(sub.status, oldGrade, sub.submission.grade);
    
    this.assignmentService.updateSubmission(sub.submission).subscribe(s => {
      this.updateStatistics(oldGrade, s.grade);
      this.studentsList[s.lmsUserId].submission = s;
    });
  }

  updateGradesToPostCount(subCurrStatus : SubmissionStatus, oldGrade : number, newGrade : number){    
    if((subCurrStatus == SubmissionStatus.PUBLISHED && newGrade) || (oldGrade == undefined && newGrade)){
      this.gradesToPost++;
    }
    else if(oldGrade && newGrade == undefined){
      this.gradesToPost--;
    }
  }

  isSortedBy = (field : string, dir : 'asc' | 'desc') => this.sortBy.field == field && this.sortBy.dir == dir;

  sort(field? : string, dir? : 'asc' | 'desc'){
    if(this.isSortedBy(field, dir)){
      return;
    }

    this.sortBy.field = field || this.sortBy.field;
    this.sortBy.dir = dir || this.sortBy.dir;

    this.filteredStudentsList = this.utils.sortComplex(this.filteredStudentsList, this.sortBy);
  }

  openStudentSubmission = (s : StudentSubmission) => this.utils.openInNewTab(this.addToUrl(document.location.href, "/submissions/" + s.submission.id));

  addToUrl(url: string, path: string) {
    if (url.indexOf('?') > 0) {
      return url.replace('?', path + '?');
    }

    return url + path;
  }
  refresh = () => {
    this.busy = true;
    this.filteredStudentsList = [];
    this.recalcStats = true;
    this.loadSubmissions();
  }

  startGrading = () => this.utils.openInNewTab(this.startGradingUrl);

  updateStatistics(oldGrade : number, newGrade : number){
    if(!oldGrade && newGrade){
      this.asgmt.statistics.graded++;
      this.asgmt.statistics.needToGrade--;
    }

    if(oldGrade && !newGrade){
      this.asgmt.statistics.graded--;
      this.asgmt.statistics.needToGrade++;
    }

    this.asgmt.statistics.avg = this.utils.recalcAverage(this.asgmt.statistics.avg, this.asgmt.statistics.graded, oldGrade, newGrade);
  }

  recalcStatsNumbers(){
    this.asgmt.statistics.graded = 0;
    this.asgmt.statistics.needToGrade = 0;

    let totalScore = 0, submitted = 0;
    Object.values(this.studentsList).filter(student => student.submission).forEach(student => {
      let sub = student.submission;
      if(sub.grade != undefined){
        this.asgmt.statistics.graded++;
        totalScore += sub.grade;
      }else{
        this.asgmt.statistics.needToGrade++;
      }
      submitted++;
    });

    this.asgmt.statistics.avg = this.asgmt.statistics.graded > 0 ? totalScore/this.asgmt.statistics.graded : 0;
    this.asgmt.statistics.didntSubmit = this.asgmt.statistics.total - (this.asgmt.statistics.graded + this.asgmt.statistics.needToGrade);
    this.recalcStats = false;
  }

  getStatusClass = (s : SubmissionStatus) => {
    switch(s){
      case SubmissionStatus.NOT_SUBMITTED : return 'label-Declined';
      case SubmissionStatus.NOT_GRADED : return 'label-warning';
      case SubmissionStatus.GRADED : return 'label-ReadyForProcessing';
      case SubmissionStatus.PUBLISHED : return 'label-success';
    }
  }

  initPostGradesPopup(){
    let confirmBtn= new CustomButton("btn.confirm", () => {
      this.blockView = true;
      this.gradesToPost = 0;
      this.lti.postGrades(this.asgmt.id).subscribe(s => {
        this.setSubmissions(s);
        this.blockView = false;
      });
    }, "left");
    confirmBtn.focus = true;
    confirmBtn.main = true;

    let cancelBtn= new CustomButton("btn.cancel", undefined, "right");

    this.postGradesConfirm.buttons = [confirmBtn, cancelBtn];
  }

  isLateSubmission = (sub : Submission) => sub && (this.asgmt.dueDate && this.dateService.isDateAfter(sub.userSubmissionDate, this.asgmt.dueDate));

  validateInput = (score : any) : boolean => !score || (score <= this.asgmt.lmsAssignmentMaxScore && score >= 0);

}
